import React, { useState, type ReactElement, useEffect } from 'react'
import { useLocation } from 'react-router'
import theme from 'common/utils/theme'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { ReactComponent as AuctionIcon } from 'assets/auction.svg'
import { CircularProgress, Divider, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { ProposalStatus, StatusInfoName } from 'common/tenders/tenders.types'
import ThemeButton from 'common/ui/theme-button'
import { StoreStatus } from 'common/utils/types'
import { useParams } from 'react-router-dom'
import { isAfter } from 'date-fns'
import FileUploader from 'features/vendor/files/file-uploader'
import VendorInfoPanel from 'features/vendor/vendor-profile/components/vendor-info-panel'
import { type GeneratedFile, GeneratedFileType } from 'common/files/files.types'
import { downloadFile, getFileUrl } from 'common/files/files.service'
import { ReactComponent as DownloadIcon } from 'assets/downloadIcon.svg'
import { ReactComponent as ClearIcon } from 'assets/clearIcon.svg'
import { ReactComponent as InfoIcon } from 'assets/infoIcon.svg'
import ThemeDatePicker from 'common/ui/date-range-picker/components/ThemeDatePicker'
import { type Topic, type UpsertProposalRequest } from 'features/bids/bid.types'
import { BUTTON_VARIANTS } from 'common/ui/theme-button/ThemeButton'
import { submitProposal, upsertProposal } from 'features/bids/bid.service'
import ThemeSnackbar, { SNACKBAR_VARIANTS } from 'common/ui/theme-snackbar/ThemeSnackbar'
import SimpleTooltip from 'common/ui/simple-tooltip'
import { getStatusInfo } from 'features/bids/accepted-bids/accepted-bid-details/components/TenderCard'
import { ProposalDetailsFromRoute } from 'features/vendor/my-offers-container/my-offers/MyOffers'

import styles from './ProposalDetails.module.scss'
import ProposalTopicsGrid from './proposal-topics-grid/ProposalTopicsGrid'

const ProposalDetails: React.FC = (): ReactElement => {
  const { palette } = useTheme()
  const { tenderId } = useParams()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const text = location.state?.disabledText
  const { submitProposalStatus, proposal, upsertProposalStatus } = useAppSelector((state) => state.bid)
  const isProposalSent = proposal?.status === ProposalStatus.SUBMITTED
  const hasDeadlinePassed = isAfter(new Date(), new Date(proposal?.tender?.proposalDeadline ?? 0))

  const previousRoute = location?.state?.source

  const tenderStatus = getStatusInfo(
    palette,
    proposal?.tender?.status,
    proposal?.status,
    proposal?.tender?.proposalDeadline,
  )

  const [startDate, setStartDate] = useState<null | Date>(
    proposal?.startDate ? new Date(proposal?.startDate) : null,
  )
  const [endDate, setEndDate] = useState<null | Date>(
    proposal?.deadline ? new Date(proposal?.deadline) : null,
  )
  const [loading, setLoading] = useState<boolean>(false)
  const [file, setFile] = useState<GeneratedFile | undefined>(proposal?.document)
  const [topicsArray, setTopicsArray] = useState<Topic[]>([])
  const [errorGrid, setErrorGrid] = useState<string>('')
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)

  useEffect(() => {
    if (!proposal?.id && tenderId) {
      void dispatch(upsertProposal({ tenderId }))
    }
  }, [proposal, tenderId])

  useEffect(() => {
    if (proposal?.id && tenderId) {
      setStartDate(proposal.startDate ? new Date(proposal.startDate) : null)
      setEndDate(proposal.deadline ? new Date(proposal.deadline) : null)
      setFile(proposal.document)
      setTopicsArray(proposal.topics ?? [])
    }
  }, [proposal, tenderId])

  const submitValidation = (updatedProposal: Partial<UpsertProposalRequest>): boolean => {
    if (startDate && startDate < new Date()) {
      setErrorGrid('Datum početka mora biti nakon današnjeg datuma')
      return false
    }
    if (
      !updatedProposal.startDate ||
      !updatedProposal.deadline ||
      !updatedProposal.document ||
      topicsArray.length !== proposal?.tender?.topics?.length
    ) {
      setErrorGrid('Morate popuniti sva polja pre slanja ponude')
      return false
    }
    if (topicsArray.some((topic: Topic) => !(topic.unit?.type && topic.unit?.count && topic.unit?.price))) {
      setErrorGrid('Morate popuniti ceo budžet pre slanja ponude')
      return false
    }
    return true
  }

  const onStartChange = (date: Date | null): void => {
    setStartDate((sd) => (sd === date ? null : date))
    if (date && endDate && endDate.toISOString() < date.toISOString()) {
      setEndDate(null)
    }
  }

  const onEndChange = (date: Date | null): void => {
    setEndDate((ed) => (ed === date ? null : date))
    if (startDate && date && date.toISOString() < startDate.toISOString()) {
      setStartDate(null)
    }
  }

  const handleUpsertProposal = (): void => {
    const filteredTopics = topicsArray.filter((topic: Topic) => {
      return topic?.unit?.type && topic?.unit?.count && topic?.unit?.price
    })
    setErrorGrid('')
    const payload: Partial<UpsertProposalRequest> = {
      deadline: endDate?.toISOString(),
      startDate: startDate?.toISOString(),
      document: file ?? undefined,
      topics: filteredTopics.length ? filteredTopics : undefined,
    }
    if (tenderId) {
      void dispatch(upsertProposal({ tenderId, payload })).then((action) => {
        if (upsertProposal.fulfilled.match(action)) {
          setIsError(false)
          setOpenSnackbar(true)
        } else if (upsertProposal.rejected.match(action)) {
          setIsError(true)
          setOpenSnackbar(true)
        }
      })
    }
  }

  const handleRefDocChange = (files: GeneratedFile[]): void => {
    if (files[0]) {
      setFile(files[0])
    }
  }

  const handleDeleteRefDoc = (): void => {
    setFile(undefined)
  }

  const handleDownloadRefDoc = async (): Promise<void> => {
    setLoading(true)
    if (file?.key) {
      try {
        const generatedFile = await getFileUrl({
          key: file.key,
          mode: 'r',
        })
        await downloadFile(GeneratedFileType.ProposalDoc, generatedFile)
      } catch (error) {
        console.error('Error while handling download:', error)
      } finally {
        setLoading(false)
      }
    }
  }

  const handleSubmit = (): void => {
    const filteredTopics = topicsArray.filter(
      (topic: Topic) => topic.unit?.type && topic.unit?.count && topic.unit?.price,
    )
    setErrorGrid('')

    const payload: Partial<UpsertProposalRequest> = {
      deadline: endDate?.toISOString(),
      startDate: startDate?.toISOString(),
      document: file ?? undefined,
      topics: filteredTopics.length ? filteredTopics : undefined,
    }

    if (tenderId) {
      void dispatch(upsertProposal({ tenderId, payload })).then((action) => {
        if (upsertProposal.fulfilled.match(action)) {
          if (submitValidation(payload)) {
            void dispatch(submitProposal({ tenderId }))
            setIsError(false)
            setOpenSnackbar(true)
          } else {
            setOpenSnackbar(true)
            setIsError(true)
          }
        } else if (upsertProposal.rejected.match(action)) {
          setIsError(true)
          setOpenSnackbar(true)
        }
      })
    }
  }

  return (
    <div className={styles.container}>
      <VendorInfoPanel
        title="POŠALJI PONUDU"
        icon={<AuctionIcon fill={theme.palette.green.main} />}
        className={styles.referentDocumentsPanel}
        contentClassName={styles.documents}
        rightHeader={
          previousRoute === 'my-offers' ? (
            <div className={styles.tooltip}>
              <SimpleTooltip title={tenderStatus.tooltipText ?? '-'} style={{ textAlign: 'left' }}>
                <div className={styles.tenderStatusInfo}>
                  <Typography variant="subtitle1" color={palette.gray.main}>
                    Status
                  </Typography>
                  <Typography variant="title3" color={tenderStatus.color}>
                    {tenderStatus.name}
                  </Typography>
                  <InfoIcon width={16} height={16} fill={palette.gray.main} />
                </div>
              </SimpleTooltip>
            </div>
          ) : (
            <div />
          )
        }
      >
        <div className={styles.upperSide}>
          <div className={styles.date}>
            <div className={styles.textWrapper}>
              <Typography variant="title1" sx={{ marginBottom: '7px', color: palette.white.main }}>
                Datumi
              </Typography>
              <Typography variant="subtitle1">
                U poljima ispod upišite datum i očekivano vreme završetka implementacije.
              </Typography>
            </div>
            <Divider className={styles.divider} flexItem />
            <div className={styles.dateRange}>
              <div className={styles.dateItem}>
                <Typography variant="subtitle1">Datum početka</Typography>
                {isProposalSent ? (
                  <div className={styles.valueWrapper}>
                    <Typography variant="subtitle2" sx={{ color: palette.blue100.main }}>
                      {startDate?.toLocaleDateString()}
                    </Typography>
                  </div>
                ) : (
                  <ThemeDatePicker
                    error={!startDate && errorGrid !== ''}
                    onClick={onStartChange}
                    value={startDate}
                    inputWidth="260px"
                  />
                )}
              </div>
              <div className={styles.dateItem}>
                <Typography variant="subtitle1">Datum završetka</Typography>
                {isProposalSent ? (
                  <div className={styles.valueWrapper}>
                    <Typography variant="subtitle2" sx={{ color: palette.blue100.main }}>
                      {endDate?.toLocaleDateString()}
                    </Typography>
                  </div>
                ) : (
                  <ThemeDatePicker
                    error={!endDate && errorGrid !== ''}
                    onClick={onEndChange}
                    value={endDate}
                    inputWidth="260px"
                  />
                )}
              </div>
            </div>
          </div>
          <div className={styles.documentation}>
            <div className={styles.textWrapper}>
              <Typography variant="title1" sx={{ marginBottom: '7px', color: palette.white.main }}>
                Dokumentacija
              </Typography>
              <Typography variant="subtitle1">
                U polju za unos ispod, okačite dokument za Vašu ponudu. Dokument mora biti u PDF formatu.
              </Typography>
            </div>
            <Divider className={styles.divider} flexItem />
            <div className={styles.documentsContainer}>
              <div className={styles.documentsWrapper}>
                {file && (
                  <div className={styles.uploadDocument}>
                    <div
                      className={styles.iconContainer}
                      onClick={() => {
                        void handleDownloadRefDoc()
                      }}
                    >
                      {loading ? (
                        <div className={styles.loader}>
                          <CircularProgress size={15} />
                        </div>
                      ) : (
                        <DownloadIcon height="16px" width="16px" fill={theme.palette.blue100.main} />
                      )}
                    </div>
                    <div className={styles.documentContent}>
                      <Typography variant="subtitle1" className={styles.truncate}>
                        {file?.filename}
                      </Typography>
                      {!isProposalSent && (
                        <ClearIcon
                          onClick={handleDeleteRefDoc}
                          stroke={theme.palette.white.main}
                          height="16px"
                          width="16px"
                          style={{ cursor: 'pointer' }}
                        />
                      )}
                    </div>
                  </div>
                )}
                {!file && !isProposalSent && (
                  <div className={styles.file}>
                    <FileUploader
                      onFileChange={handleRefDocChange}
                      multiple={false}
                      fileType={GeneratedFileType.ProposalDoc}
                      placeholder="Možete da okačite 1 dokument"
                      accept=".pdf"
                      proposalId={proposal?.id}
                      disabled={!proposal}
                      error={errorGrid !== '' && !file}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <ProposalTopicsGrid errorGrid={errorGrid} topicsArray={topicsArray} setTopicsArray={setTopicsArray} />
        {(previousRoute !== ProposalDetailsFromRoute.MY_OFFERS ||
          (previousRoute === ProposalDetailsFromRoute.MY_OFFERS &&
            tenderStatus.name === StatusInfoName.DRAFT)) &&
        !hasDeadlinePassed ? (
          <div className={styles.footer}>
            <div className={styles.button}>
              {!isProposalSent && (
                <ThemeButton
                  disabled={isProposalSent || text}
                  onClick={handleUpsertProposal}
                  variant={BUTTON_VARIANTS.secondary}
                  sx={{ color: palette.white.main }}
                  startIcon={
                    upsertProposalStatus === StoreStatus.Loading ? (
                      <CircularProgress size={16} color="primary" />
                    ) : null
                  }
                >
                  SNIMI ZA POSLE
                </ThemeButton>
              )}
              <ThemeButton
                disabled={isProposalSent || text}
                onClick={handleSubmit}
                sx={{ color: palette.white.main }}
                startIcon={
                  submitProposalStatus === StoreStatus.Loading ? (
                    <CircularProgress size={16} color="primary" />
                  ) : null
                }
              >
                {isProposalSent ? 'PONUDA POSLATA' : 'POŠALJI PONUDU'}
              </ThemeButton>
            </div>
            {text && <Typography variant="title4">{text}</Typography>}
          </div>
        ) : (
          <div />
        )}
      </VendorInfoPanel>
      <ThemeSnackbar
        snackbarOpen={openSnackbar}
        variant={isError ? SNACKBAR_VARIANTS.error : SNACKBAR_VARIANTS.success}
        message={isError ? 'Greška pri snimanju' : 'Uspešno snimljeno'}
        onClose={() => {
          setOpenSnackbar(false)
        }}
      />
    </div>
  )
}

export default ProposalDetails
